import type { RunChannel } from './validate-runs';

/**
 * Suite schedules — the testing tool's scheduled runs (`apps/tester`:
 * `runAfter` and recurring `test_schedules`). A schedule runs a suite on the
 * server, so it needs nobody's browser; its runs land in the solution's shared
 * history like any other.
 *
 * Times are the scheduler's wall clock in its own time zone ("every weekday at
 * 9:00 in New York"), so they keep meaning that across daylight saving.
 * Pure, so the page and the server compute the same next run.
 */

export type ScheduleRepeat =
  | 'once'
  | 'hourly'
  | 'daily'
  | 'weekdays'
  | 'weekly';

export const REPEATS: { value: ScheduleRepeat; label: string }[] = [
  { value: 'once', label: 'Once' },
  { value: 'hourly', label: 'Every hour' },
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Every weekday' },
  { value: 'weekly', label: 'Every week' },
];

export const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export interface SuiteSchedule {
  id: string;
  suiteId: string;
  /** The suite's name when scheduled, for the list. */
  suiteName: string;
  repeat: ScheduleRepeat;
  /** "HH:MM" on the clock in `timeZone`; an hourly schedule uses the minutes. */
  time: string;
  /** 0 Sunday … 6 Saturday, for a weekly schedule. */
  weekday?: number;
  /** "YYYY-MM-DD", for a one-off. */
  date?: string;
  /** IANA zone, e.g. "America/New_York". */
  timeZone: string;
  enabled: boolean;
  /** Epoch ms of the next run; null once a one-off has run or it's off. */
  nextRunAt: number | null;
  lastRunAt?: number | null;
  lastSuiteRunId?: string | null;
  /** Why the scheduler skipped its last run (a spent model-call budget), until it next runs. */
  skipReason?: string;
  /** Where it runs: the suite's channel, as it was when scheduled. */
  channel: RunChannel;
  /** The Pro Studio it was scheduled from, which picks the environment. */
  origin: string;
  /** The solution's Quality score judge then, so the scores match the page's. */
  judge?: unknown;
  createdAt: number;
  updatedAt: number;
}

const DAY = 24 * 60 * 60 * 1000;

interface Parts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/** The wall clock in `timeZone` at `ts`. */
export const partsIn = (ts: number, timeZone: string): Parts => {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    weekday: 'short',
  });
  const get = (type: string) =>
    fmt.formatToParts(new Date(ts)).find((p) => p.type === type)?.value ?? '';
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
    weekday: WEEKDAY_INDEX[get('weekday')] ?? 0,
  };
};

/** Epoch ms of a wall-clock time in `timeZone`. */
export const zonedTime = (
  { year, month, day, hour, minute }: Omit<Parts, 'weekday'>,
  timeZone: string
): number => {
  const asUtc = Date.UTC(year, month - 1, day, hour, minute);
  const offsetAt = (ts: number) => {
    const p = partsIn(ts, timeZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - ts;
  };
  // Twice, so a guess on the far side of a DST change settles.
  let ts = asUtc - offsetAt(asUtc);
  ts = asUtc - offsetAt(ts);
  return ts;
};

const clock = (time: string): { hour: number; minute: number } => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  const hour = m ? Math.min(23, Number(m[1])) : 9;
  const minute = m ? Math.min(59, Number(m[2])) : 0;
  return { hour, minute };
};

/** The next run strictly after `after`, or null when there isn't one. */
export const nextRunAfter = (
  s: Pick<
    SuiteSchedule,
    'repeat' | 'time' | 'weekday' | 'date' | 'timeZone' | 'enabled'
  >,
  after: number
): number | null => {
  if (!s.enabled) return null;
  const { hour, minute } = clock(s.time);
  if (s.repeat === 'once') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.date ?? '');
    if (!m) return null;
    const at = zonedTime(
      {
        year: Number(m[1]),
        month: Number(m[2]),
        day: Number(m[3]),
        hour,
        minute,
      },
      s.timeZone
    );
    return at > after ? at : null;
  }
  if (s.repeat === 'hourly') {
    const p = partsIn(after, s.timeZone);
    const at = zonedTime({ ...p, minute }, s.timeZone);
    return at > after ? at : at + 60 * 60 * 1000;
  }
  for (let i = 0; i <= 8; i += 1) {
    const p = partsIn(after + i * DAY, s.timeZone);
    const ok =
      s.repeat === 'daily' ||
      (s.repeat === 'weekdays' && p.weekday >= 1 && p.weekday <= 5) ||
      (s.repeat === 'weekly' && p.weekday === (s.weekday ?? 1));
    if (ok) {
      const at = zonedTime({ ...p, hour, minute }, s.timeZone);
      if (at > after) return at;
    }
  }
  return null;
};

/** "Every weekday at 09:00 (New York)". */
export const describeSchedule = (
  s: Pick<SuiteSchedule, 'repeat' | 'time' | 'weekday' | 'date' | 'timeZone'>
): string => {
  const zone = s.timeZone.split('/').pop()?.replace(/_/g, ' ') ?? s.timeZone;
  const { hour, minute } = clock(s.time);
  const hhmm = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  switch (s.repeat) {
    case 'once':
      return `Once, ${s.date ?? '—'} at ${hhmm} (${zone})`;
    case 'hourly':
      return `Every hour at :${String(minute).padStart(2, '0')}`;
    case 'weekly':
      return `Every ${WEEKDAYS[s.weekday ?? 1]} at ${hhmm} (${zone})`;
    case 'weekdays':
      return `Every weekday at ${hhmm} (${zone})`;
    default:
      return `Every day at ${hhmm} (${zone})`;
  }
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** A schedule as storage hands it back, or null when it isn't one. */
export const parseSchedule = (v: unknown): SuiteSchedule | null => {
  if (
    !isRecord(v) ||
    typeof v.id !== 'string' ||
    typeof v.suiteId !== 'string' ||
    !REPEATS.some((r) => r.value === v.repeat) ||
    typeof v.time !== 'string' ||
    typeof v.timeZone !== 'string' ||
    !isRecord(v.channel) ||
    typeof v.channel.appId !== 'string' ||
    typeof v.origin !== 'string'
  ) {
    return null;
  }
  return v as unknown as SuiteSchedule;
};
